import { ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { TSvgProps } from '@r1-frontend/ui-react/components/svg/createSvg';
import Svg from '@r1-frontend/ui-react/components/svg/Svg';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';
import { FONTS } from '@r1-frontend/ui-react/tokens/fonts';
import { useMatchMedia } from '@r1-frontend/shared/hooks/useMatchMedia';

export const controlElementTypes = ['small', 'medium', 'large'] as const;

export type TControlElementType = typeof controlElementTypes[number];

export type TControlElementProps = {
    type?: TControlElementType,
    $customFont?: FONTS.MediumS | FONTS.MediumXS | FONTS.MediumXXS,
    Icon?: (props: TSvgProps) => JSX.Element,
    children?: string | ReactNode | JSX.Element,
    onClick?: () => void,
}

const ControlElement = (
    {
        children,
        Icon,

        ...rest
    }: Pick<TControlElementProps, 'Icon' | 'children' | 'type'>,
): JSX.Element => {
    const isMobile = useMatchMedia(`(max-width: ${wideBreakpoints.mobile})`);
    return (
        <strong {...rest}>
            {Boolean(children) && children}
            {Icon && (<Icon size={isMobile ? 16 : 20}/>)}
        </strong>
    );
};

const getFontByType = (type: TControlElementType): { mobile: FONTS, desktop: FONTS } => {
    switch (type) {
        case 'large': {
            return { mobile: FONTS.MediumXS, desktop: FONTS.MediumS };
        }
        case 'medium': {
            return { mobile: FONTS.MediumXXS, desktop: FONTS.MediumXS };
        }
        case 'small': {
            return { mobile: FONTS.MediumXXS, desktop: FONTS.MediumXXS };
        }
    }
};

const StyledControlElement = styled(ControlElement)<TControlElementProps>`
    display: inline-flex;

    justify-content: flex-start;
    align-items: center;
    flex-direction: row;
    gap: 5px;

    color: ${COLORS.Link};
    cursor: pointer;

    ${Svg} {
        #guts, #contour {
            stroke: ${COLORS.Link};
        }
    }

    ${({ type = 'large', $customFont }) => {
        if ($customFont) {
            return css`${$customFont};`;
        }

        if (!$customFont && type) {
            const { mobile, desktop } = getFontByType(type);

            return css`
                ${mobile};

                @media (min-width: ${wideBreakpoints.mobile}) {
                    ${desktop};
                }
            `;
        }
    }}
`;

export default StyledControlElement;
